import * as React from 'react'
import {
  Drawer, Box, Typography, IconButton, Button, TextField, MenuItem, Chip, Tooltip, Alert,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'

import {primaryBtnSx, secondaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, PRIMARY_BLUE} from '../../../styles/foundation/colors'
import {GAP} from '../../../styles/foundation/spacing'

import {evidenceScore} from '../shared/evidenceScore'
import {kg as fmtKg, pct as fmtPct, int as fmtInt} from './format'
import {round2, getVolumetricDivisor} from './weightMath'
import {DWS_SLOTS, DWS_COURIERS, MISSING_LABEL, DWS_RETAIN} from './data/dwsCopy'
import {uploadMissing, uploadTodo, validateImage, validateVideo, buildUploadRecord} from './state/dwsUpload'
import {dwsRecordsFor} from './state/dwsEvidence'
import DwsGauge, {LEVEL_COLOR, LEVEL_WORD} from './components/DwsGauge'
import DwsPlate from './components/DwsPlate'
import DropZone from './components/DropZone'
import KeyValue from './components/KeyValue'
import {chipSx} from './components/tone'

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const sectionLabelSx = {fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, mb: 1}
const alertSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75, '& .MuiAlert-message': {lineHeight: 1.55}}

/**
 * Every field in section 3 is styled once, the shape the two Initiate drawers
 * use.
 *
 * Project rule 16: MUI leaks theme.typography.body1's 16px into
 * `.MuiInputBase-input` and `.MuiSelect-select`, and it beats inheritance, so
 * a fontSize on the field root never reaches the text a merchant actually
 * types into. Both are beaten here.
 */
const fieldSx = {
  fontFamily: FONT,
  fontSize: SIZE.xs,
  '& .MuiInputBase-input': {fontFamily: FONT, fontSize: SIZE.xs},
  '& .MuiSelect-select': {fontFamily: FONT, fontSize: SIZE.xs},
}

/**
 * A Select's menu is PORTALLED to document.body, outside this drawer's subtree,
 * so `fieldSx`'s descendant selectors never reach it and its items keep
 * body1's 1rem — the SKU list opens a third larger than the field that
 * summoned it. Same shape and same SIZE['2xs'] as the module's other menus.
 */
const selectMenuProps = {
  PaperProps: {sx: {'& .MuiMenuItem-root': {fontFamily: FONT, fontSize: SIZE['2xs']}}},
}

/**
 * The Evidence Studio, rebuilt for a freeze — Task 6. Same scoring, same
 * five named image slots, same gauge as the Weight Dispute module's own
 * Evidence Studio (disputes-v2/EvidenceStudio.js): "a merchant should not
 * have to learn two different ways to send Prozo a photograph of a parcel."
 * What differs is the subject — this argues a single shipment's SKU/AWB up
 * front, rather than a batch of already-disputed AWBs after the fact.
 *
 * `rows` is the shared store's own `skus` slice (see DwsPage.js, which reads
 * it straight off state/useFreezeStore.js — no frozen import, no workaround)
 * and `uploads` is the store's session-only upload list, threaded through
 * only to (a) preview "what the scanner already recorded" for the chosen SKU
 * via dwsRecordsFor, and (b) mint a collision-free monotonic sequence number
 * for the next record's id.
 *
 * `onSaved(record, score)` is called on Save; this drawer does not push a
 * toast itself — DwsPage owns that, so the message can name the SKU's new
 * count and this component does not need to know about notistack at all.
 */
export default function DwsUploadDrawer({open, rowId, rows, uploads, onClose, onSaved}) {
  const [skuCode, setSkuCode] = React.useState(rowId || '')
  const [video, setVideo] = React.useState(null)
  const [images, setImages] = React.useState({})
  const [awb, setAwb] = React.useState('')
  const [courier, setCourier] = React.useState('')
  const [weightKg, setWeightKg] = React.useState('')
  const [dim, setDim] = React.useState({l: '', b: '', h: ''})
  const [note, setNote] = React.useState('')
  const [errors, setErrors] = React.useState({})
  const [dragKey, setDragKey] = React.useState(null)

  const videoSectionRef = React.useRef(null)
  const photoSectionRef = React.useRef(null)
  const shipmentSectionRef = React.useRef(null)

  // Blob urls are tracked in a ref, not in state, so the unmount cleanup
  // still sees every url this drawer ever created even if the final render
  // dropped them. The mockup created one per file pick and revoked none,
  // ever.
  const blobUrls = React.useRef([])
  const trackUrl = (file) => {
    const url = URL.createObjectURL(file)
    blobUrls.current.push(url)
    return url
  }
  const releaseUrl = (url) => {
    if (!url) return
    URL.revokeObjectURL(url)
    blobUrls.current = blobUrls.current.filter((u) => u !== url)
  }

  // Reset to a blank form on the closed -> open transition, same
  // "adjusted during render" pattern ReviewSkuDrawer/UploadDrawer use — not
  // on every render, or a keystroke in the AWB field would wipe itself. Any
  // urls left over from a PREVIOUS visit that was cancelled without saving
  // are revoked here too: this is the one point that can honestly call
  // every slot "cleared at once".
  const [prevOpen, setPrevOpen] = React.useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      blobUrls.current.forEach((u) => URL.revokeObjectURL(u))
      blobUrls.current = []
      setSkuCode(rowId || '')
      setVideo(null)
      setImages({})
      setAwb('')
      setCourier('')
      setWeightKg('')
      setDim({l: '', b: '', h: ''})
      setNote('')
      setErrors({})
    }
  }

  React.useEffect(() => () => {
    blobUrls.current.forEach((u) => URL.revokeObjectURL(u))
    blobUrls.current = []
  }, [])

  const row = (rows || []).find((r) => r.sku === skuCode) || null
  const leftRecords = row ? dwsRecordsFor(row, uploads) : []

  const missing = uploadMissing({row, video, images, awb, courier, kg: weightKg})
  const todo = uploadTodo({video, images, awb, courier, note})
  const stillNeeded = missing.map((k) => MISSING_LABEL[k])

  const filled = Object.keys(images).length
  const identified = !!(String(awb || '').trim() && courier)
  const remark = !!String(note || '').trim()
  const score = evidenceScore({video: !!video, imageSlots: filled, category: identified, remark})
  const videoGain = (todo.find((t) => t.key === 'video') || {}).gain || 0

  const takeVideo = (file) => {
    const check = validateVideo(file)
    if (!check.ok) {
      setErrors((e) => ({...e, video: check.message}))
      return
    }
    setErrors((e) => {
      const next = {...e}
      delete next.video
      return next
    })
    setVideo((prev) => {
      if (prev && prev.url) releaseUrl(prev.url)
      return {name: file.name, size: file.size, url: trackUrl(file)}
    })
  }

  const clearVideo = () => {
    setVideo((prev) => {
      if (prev && prev.url) releaseUrl(prev.url)
      return null
    })
  }

  // Replacing a file in a slot must revoke the url the old one held, or the
  // blob stays alive for the life of the tab.
  const takeImage = (slotKey, file) => {
    const check = validateImage(file)
    if (!check.ok) {
      setErrors((e) => ({...e, [slotKey]: check.message}))
      return
    }
    setErrors((e) => {
      const next = {...e}
      delete next[slotKey]
      return next
    })
    setImages((prev) => {
      if (prev[slotKey]) releaseUrl(prev[slotKey].url)
      return {...prev, [slotKey]: {name: file.name, size: file.size, url: trackUrl(file)}}
    })
  }

  const clearImage = (slotKey) => {
    setImages((prev) => {
      if (!prev[slotKey]) return prev
      releaseUrl(prev[slotKey].url)
      const next = {...prev}
      delete next[slotKey]
      return next
    })
  }

  const pickFrom = (e) => {
    const f = e.target.files && e.target.files[0]
    e.target.value = ''
    return f || null
  }

  const dragOver = (key) => (e) => { e.preventDefault(); setDragKey(key) }
  const dragLeave = (key) => (e) => {
    e.preventDefault()
    setDragKey((k) => (k === key ? null : k))
  }
  const dropOn = (key, take) => (e) => {
    e.preventDefault()
    setDragKey(null)
    const f = e.dataTransfer.files && e.dataTransfer.files[0]
    if (f) take(f)
  }

  const scrollToRef = (ref) => { if (ref && ref.current) ref.current.scrollIntoView({behavior: 'smooth', block: 'start'}) }
  const handleTodoClick = (key) => {
    if (key === 'video') scrollToRef(videoSectionRef)
    else if (key.indexOf('img:') === 0) scrollToRef(photoSectionRef)
    else scrollToRef(shipmentSectionRef)
  }

  const l = Number(dim.l); const b = Number(dim.b); const h = Number(dim.h)
  const dimsTyped = l > 0 && b > 0 && h > 0
  const liveVol = dimsTyped ? round2((l * b * h) / getVolumetricDivisor()) : null

  const handleSave = () => {
    if (missing.length > 0 || !row) return
    // `uploads.length + 1` rather than a ref-held counter: this drawer (and
    // the page around it) unmounts on every rail click, but the store's own
    // `uploads` array does not, so deriving the next id from its length is
    // what keeps DWS-UP-S ids collision-free across a navigate-away-and-back.
    const seq = (uploads || []).length + 1
    const record = buildUploadRecord({row, awb, courier, kg: weightKg, dim, note, video, images}, seq)

    // The saved urls now belong to the committed record — DwsThumb and
    // DwsRecordDrawer will render them from the store from here on. Stop
    // tracking them so this drawer's own unmount cleanup never revokes a
    // url something else is still displaying.
    const savedUrls = [video && video.url].concat(Object.keys(images).map((k) => images[k].url)).filter(Boolean)
    blobUrls.current = blobUrls.current.filter((u) => !savedUrls.includes(u))

    onSaved(record, score)
  }

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', md: 820}, maxWidth: '100%'}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
              Evidence Studio
            </Typography>
            <Typography sx={labelSx}>
              Argue this shipment&apos;s weight the same way you would a dispute — a video or stills, against
              the scanner&apos;s own reading.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label='Close evidence studio' size='small'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>

        <Box sx={{
          p: 2, overflowY: 'auto', flex: 1,
          display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'minmax(0,300px) minmax(0,1fr)'}, gap: GAP.lg,
        }}
        >
          {/* Left column — what the scanner already recorded. */}
          <Box sx={{display: 'grid', gap: 1.5, alignContent: 'start'}}>
            <Typography sx={sectionLabelSx}>What the scanner already recorded</Typography>
            <Typography sx={labelSx}>
              {!row
                ? 'Choose a SKU on the right to see what the scanner already recorded.'
                : leftRecords.length
                  ? `${fmtInt(leftRecords.length)} record${leftRecords.length === 1 ? '' : 's'} on file for ${row.sku}.`
                  : `No scanner records on file yet for ${row.sku}.`}
            </Typography>

            {leftRecords.slice(0, 2).map((r) => (
              <Box key={r.id} sx={{border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden'}}>
                <DwsPlate record={r} />
              </Box>
            ))}

            {row && (
              <KeyValue items={[
                {label: 'Most probable weight', value: fmtKg(row.mpw)},
                {label: 'Dead weight', value: fmtKg(row.deadKg)},
                {label: 'Volumetric', value: fmtKg(row.volKg)},
                {label: 'Weight spread', value: fmtPct(row.spread)},
              ]}
              />
            )}

            <Alert severity='warning' sx={alertSx}>{DWS_RETAIN}</Alert>
          </Box>

          {/* Right column — build your evidence. */}
          <Box sx={{display: 'grid', gap: 2, alignContent: 'start'}}>
            <Typography sx={sectionLabelSx}>Build your evidence</Typography>

            <Box sx={{display: 'flex', gap: 2, alignItems: 'center', border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1.5}}>
              <DwsGauge score={score.score} level={score.level} />
              <Box>
                {/* The footer prints this same verdict word; the two are one
                    size so a merchant reads them as the same statement. */}
                <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: LEVEL_COLOR[score.level]}}>
                  {LEVEL_WORD[score.level]}
                </Typography>
                <Typography sx={labelSx}>
                  About {score.win}% of freeze requests with evidence like this are approved.
                </Typography>
              </Box>
            </Box>

            {todo.length > 0 && (
              <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap'}}>
                {todo.map((t) => (
                  <Chip
                    key={t.key}
                    size='small'
                    clickable
                    onClick={() => handleTodoClick(t.key)}
                    label={`${t.label} · +${t.gain}`}
                    sx={t.big ? {...chipSx('ok'), fontWeight: WEIGHT.bold} : chipSx('info')}
                  />
                ))}
              </Box>
            )}

            <Box ref={videoSectionRef}>
              <Typography sx={sectionLabelSx}>1. Packing video</Typography>
              <DropZone
                icon={VideocamOutlinedIcon}
                big
                label='Add a packing video'
                hint='One unbroken clip, ending on the scale. Settles about 97% of freeze requests on its own.'
                file={video}
                error={errors.video}
                dragActive={dragKey === 'video'}
                accept='video/mp4,video/quicktime,video/webm'
                ariaLabel='Upload the packing video'
                removeAriaLabel='Remove the packing video'
                onPick={(e) => { const f = pickFrom(e); if (f) takeVideo(f) }}
                onClear={clearVideo}
                onDragOver={dragOver('video')}
                onDragLeave={dragLeave('video')}
                onDrop={dropOn('video', takeVideo)}
              />
            </Box>

            <Box ref={photoSectionRef}>
              <Typography sx={sectionLabelSx}>2. Photo evidence</Typography>
              <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1}}>
                {DWS_SLOTS.map((slot) => (
                  <DropZone
                    key={slot.k}
                    icon={ImageOutlinedIcon}
                    label={slot.label}
                    hint={slot.hint}
                    file={images[slot.k]}
                    error={errors[slot.k]}
                    dragActive={dragKey === `img:${slot.k}`}
                    accept='image/jpeg,image/png,image/webp'
                    ariaLabel={`Upload the ${slot.label.toLowerCase()} image`}
                    removeAriaLabel={`Remove the ${slot.label.toLowerCase()} image`}
                    onPick={(e) => { const f = pickFrom(e); if (f) takeImage(slot.k, f) }}
                    onClear={() => clearImage(slot.k)}
                    onDragOver={dragOver(`img:${slot.k}`)}
                    onDragLeave={dragLeave(`img:${slot.k}`)}
                    onDrop={dropOn(`img:${slot.k}`, (f) => takeImage(slot.k, f))}
                  />
                ))}
              </Box>
            </Box>

            <Box ref={shipmentSectionRef}>
              <Typography sx={sectionLabelSx}>3. Which shipment this is</Typography>
              <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 1.5}}>
                <TextField
                  select size='small' label='SKU' value={skuCode}
                  onChange={(e) => setSkuCode(e.target.value)}
                  sx={fieldSx}
                  SelectProps={{MenuProps: selectMenuProps}}
                >
                  <MenuItem value=''><em>Choose a SKU</em></MenuItem>
                  {(rows || []).map((r) => <MenuItem key={r.sku} value={r.sku}>{r.sku}</MenuItem>)}
                </TextField>

                <TextField
                  select size='small' label='Courier' value={courier}
                  onChange={(e) => setCourier(e.target.value)}
                  sx={fieldSx}
                  SelectProps={{MenuProps: selectMenuProps}}
                >
                  <MenuItem value=''><em>Choose a courier</em></MenuItem>
                  {DWS_COURIERS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>

                <TextField
                  size='small' label='AWB' value={awb}
                  onChange={(e) => setAwb(e.target.value)}
                  sx={fieldSx}
                />

                <TextField
                  size='small' label='Weight the scale read (kg)' value={weightKg}
                  onChange={(e) => setWeightKg(e.target.value)}
                  inputProps={{inputMode: 'decimal'}}
                  sx={fieldSx}
                />

                <TextField
                  size='small' label='Length (cm)' value={dim.l}
                  onChange={(e) => setDim((d) => ({...d, l: e.target.value}))}
                  inputProps={{inputMode: 'decimal'}}
                  sx={fieldSx}
                />
                <TextField
                  size='small' label='Breadth (cm)' value={dim.b}
                  onChange={(e) => setDim((d) => ({...d, b: e.target.value}))}
                  inputProps={{inputMode: 'decimal'}}
                  sx={fieldSx}
                />
                <TextField
                  size='small' label='Height (cm)' value={dim.h}
                  onChange={(e) => setDim((d) => ({...d, h: e.target.value}))}
                  inputProps={{inputMode: 'decimal'}}
                  sx={fieldSx}
                />
                <Typography sx={{...labelSx, alignSelf: 'center'}}>
                  {dimsTyped
                    ? `Volumetric from these dimensions: ${fmtKg(liveVol)}`
                    : 'Enter all three dimensions for a volumetric estimate — left blank, it falls back to the SKU’s packing material.'}
                </Typography>
              </Box>

              <TextField
                size='small' label='Note (optional)' value={note}
                onChange={(e) => setNote(e.target.value)}
                multiline minRows={2} fullWidth
                sx={{...fieldSx, mt: 1.5}}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0}}>
            <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: LEVEL_COLOR[score.level]}}>
              {LEVEL_WORD[score.level]}
            </Typography>
            <Typography sx={labelSx}>{score.win}% predicted approval</Typography>
            {!video && videoGain > 0 && (
              <Typography sx={{...labelSx, color: PRIMARY_BLUE}}>
                A packing video is worth +{videoGain} on its own.
              </Typography>
            )}
          </Box>

          <Box sx={{ml: 'auto', display: 'flex', gap: 1}}>
            <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>
            <Tooltip title={missing.length ? `Still needed: ${stillNeeded.join(', ')}.` : ''}>
              <Box component='span' tabIndex={missing.length ? 0 : -1} sx={{display: 'inline-flex'}}>
                <Button
                  variant='contained'
                  disabled={missing.length > 0}
                  onClick={handleSave}
                  startIcon={<UploadOutlinedIcon />}
                  aria-label={missing.length ? `Save to DWS data. Still needed: ${stillNeeded.join(', ')}.` : 'Save to DWS data'}
                  sx={primaryBtnSx}
                >
                  Save to DWS data
                </Button>
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}
